import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormRowComponent } from '../form-row/form-row.component';

// Below this rendered width the form always collapses to the 'default' (stacked) layout.
const HORIZONTAL_MIN_WIDTH_REM = 50;

@Component({
    selector: 'sc-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    standalone: false
})
export class FormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ContentChildren(FormRowComponent, { descendants: true })
  rows: QueryList<FormRowComponent>;

  @ViewChild('formContainer')
  formContainer: ElementRef;

  @Output()
  change = new EventEmitter<any>();

  @Input()
  size: 'sm' | 'md' | 'lg' = 'md';

  @Input()
  form: 'horizontal' | 'default' = 'horizontal';

  @Input()
  disabled = false;

  private _unsubscribeRows = new Subject<any>();

  private _resizeObserver: ResizeObserver;

  private _appliedForm: 'horizontal' | 'default';

  constructor() { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: any) {
    if (changes.disabled || changes.size || changes.form) {
      this.layoutRows();
    }
  }

  ngAfterViewInit() {
    this.layoutRows();
    this.rows.changes.subscribe(() => this.layoutRows());

    if (typeof ResizeObserver !== 'undefined' && this.formContainer) {
      this._resizeObserver = new ResizeObserver(() => {
        if (this.getEffectiveForm() !== this._appliedForm) {
          this.layoutRows();
        }
      });
      this._resizeObserver.observe(this.formContainer.nativeElement);
    }
  }

  ngOnDestroy() {
    this._resizeObserver?.disconnect();
    this._unsubscribeRows.next();
    this._unsubscribeRows.complete();
  }

  // Forces 'default' once the form is narrower than the horizontal layout threshold.
  getEffectiveForm(): 'horizontal' | 'default' {
    if (this.form === 'default') return 'default';

    var element = this.formContainer?.nativeElement;
    if (!element) return this.form;

    var remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return element.offsetWidth < HORIZONTAL_MIN_WIDTH_REM * remInPx ? 'default' : 'horizontal';
  }

  layoutRows()
  {
    // ngOnChanges can fire before ngAfterContentInit populates the content children.
    if (!this.rows) return;

    this._unsubscribeRows.next();

    var maxLabelLength = 0;

    this.rows.forEach(r => {
      r.size = this.size;
      if (maxLabelLength > r.label?.length) return;
      maxLabelLength = r.label.length;
    });

    var effectiveForm = this.getEffectiveForm();
    this._appliedForm = effectiveForm;

    this.rows.forEach(r => {
      r.change.pipe(takeUntil(this._unsubscribeRows)).subscribe(() => {
        this.change.next();
      });

      setTimeout(() => {
        r.form = effectiveForm;
        r.formDisabled = this.disabled;
        r.updateDisabledState();
        r.labelMaxLength = maxLabelLength + 3;
      });
    });
  }
}
